// (c) chaehongee. Confidential and proprietary.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getUsageDashboard } from '../../../services/usage'
import type {
    UsageDashboardResponse,
    UsageDailyPoint,
    UsageToolBreakdown,
    UsageModelTokens,
} from 'models-ts/usage'

import layout from './PageLayout.module.css'
import s from './Usage.module.css'

const PERIODS = [7, 30, 90] as const
type Period = (typeof PERIODS)[number]

// Known operation types, in display order. Unknown keys fall through to their
// raw name so newly-added operations still render.
const OPERATION_LABEL_KEYS: Record<string, string> = {
    tool_execution: 'usageOpToolExecution',
    llm_call: 'usageOpLlmCall',
    image_generation: 'usageOpImageGeneration',
    session_creation: 'usageOpSessionCreation',
    lora_training: 'usageOpLoraTraining',
}
const OPERATION_ORDER = Object.keys(OPERATION_LABEL_KEYS)

function formatNumber(value: number, locale: string): string {
    return new Intl.NumberFormat(locale).format(value)
}

function formatCompact(value: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(value)
}

function formatDayLabel(dateStr: string, locale: string): string {
    // date is a UTC calendar day 'YYYY-MM-DD'; parse and render as UTC to avoid drift.
    return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    })
}

// ── Period selector ──────────────────────────────────────

function PeriodSelector({
    value,
    onChange,
    disabled,
}: {
    value: Period
    onChange: (period: Period) => void
    disabled: boolean
}) {
    const { t } = useTranslation('dashboard')
    return (
        <div className={s.periodSelector} role="group" aria-label={t('usagePeriodLabel')}>
            {PERIODS.map((period) => (
                <button
                    key={period}
                    type="button"
                    className={s.periodButton}
                    data-active={period === value}
                    disabled={disabled}
                    onClick={() => onChange(period)}
                >
                    {t(`usagePeriod${period}d`)}
                </button>
            ))}
        </div>
    )
}

// ── Stat tiles ───────────────────────────────────────────

function StatTiles({ data }: { data: UsageDashboardResponse }) {
    const { t, i18n } = useTranslation('dashboard')
    const locale = i18n.language

    const tiles = [
        {
            label: t('usageCreditsUsed'),
            value: formatNumber(data.total_credits_used, locale),
        },
        {
            label: t('usageImagesGenerated'),
            value: formatNumber(data.total_images_generated, locale),
        },
        {
            label: t('usageInputTokens'),
            value: formatCompact(data.total_input_tokens, locale),
        },
        {
            label: t('usageOutputTokens'),
            value: formatCompact(data.total_output_tokens, locale),
        },
    ]

    return (
        <div className={s.tiles}>
            {tiles.map((tile) => (
                <div key={tile.label} className={s.tile}>
                    <p className={s.tileValue}>{tile.value}</p>
                    <p className={s.tileLabel}>{tile.label}</p>
                </div>
            ))}
        </div>
    )
}

// ── Daily credits chart ──────────────────────────────────

function DailyCreditsChart({ daily }: { daily: UsageDailyPoint[] }) {
    const { t, i18n } = useTranslation('dashboard')
    const locale = i18n.language

    const maxCredits = Math.max(1, ...daily.map((d) => d.credits_used))
    const creditsWord = t('usageCreditsUsed').toLowerCase()
    const firstLabel = daily.length ? formatDayLabel(daily[0].date, locale) : ''
    const lastLabel = daily.length
        ? formatDayLabel(daily[daily.length - 1].date, locale)
        : ''

    return (
        <div className={`${layout.section} ${s.chartSection}`}>
            <h3 className={s.sectionTitle}>{t('usageDailyChartTitle')}</h3>
            <div
                className={s.chart}
                role="img"
                aria-label={t('usageDailyChartAria', {
                    days: daily.length,
                    first: firstLabel,
                    last: lastLabel,
                })}
            >
                {daily.map((point) => (
                    <div key={point.date} className={s.barColumn}>
                        <div
                            className={s.bar}
                            data-empty={point.credits_used === 0}
                            style={{ height: `${(point.credits_used / maxCredits) * 100}%` }}
                            title={`${formatDayLabel(point.date, locale)} · ${formatNumber(
                                point.credits_used,
                                locale
                            )} ${creditsWord}`}
                        />
                    </div>
                ))}
            </div>
            <div className={s.chartAxis}>
                <span>{firstLabel}</span>
                <span>{lastLabel}</span>
            </div>
        </div>
    )
}

// ── Credits by tool ──────────────────────────────────────

function ToolBreakdown({ tools }: { tools: UsageToolBreakdown[] }) {
    const { t, i18n } = useTranslation('dashboard')
    const locale = i18n.language

    if (tools.length === 0) return null

    const maxCredits = Math.max(1, ...tools.map((tool) => tool.credits_used))

    return (
        <div className={`${layout.section} ${s.breakdownSection}`}>
            <h3 className={s.sectionTitle}>{t('usageByToolTitle')}</h3>
            <div className={s.breakdownList}>
                {tools.map((tool) => (
                    <div key={tool.tool_name} className={s.breakdownRow}>
                        <div className={s.breakdownHead}>
                            <span className={s.breakdownName}>{tool.tool_name}</span>
                            <span className={s.breakdownMeta}>
                                {t('usageExecutions', { count: tool.execution_count })}
                                {' · '}
                                <span className={s.mono}>
                                    {formatNumber(tool.credits_used, locale)}
                                </span>
                            </span>
                        </div>
                        <div className={s.breakdownTrack}>
                            <div
                                className={s.breakdownFill}
                                style={{
                                    width: `${(tool.credits_used / maxCredits) * 100}%`,
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// ── Tokens by model ──────────────────────────────────────

function ModelTokensTable({ models }: { models: UsageModelTokens[] }) {
    const { t, i18n } = useTranslation('dashboard')
    const locale = i18n.language

    if (models.length === 0) return null

    return (
        <div className={`${layout.section} ${s.breakdownSection}`}>
            <h3 className={s.sectionTitle}>{t('usageByModelTitle')}</h3>
            <div className={s.tableScroll}>
                <table className={s.table}>
                    <thead>
                        <tr>
                            <th className={s.th}>{t('usageModel')}</th>
                            <th className={`${s.th} ${s.thNumeric}`}>
                                {t('usageInputTokens')}
                            </th>
                            <th className={`${s.th} ${s.thNumeric}`}>
                                {t('usageOutputTokens')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {models.map((model) => (
                            <tr key={model.model}>
                                <td className={s.td}>{model.model}</td>
                                <td className={`${s.td} ${s.tdNumeric} ${s.mono}`}>
                                    {formatNumber(model.input_tokens, locale)}
                                </td>
                                <td className={`${s.td} ${s.tdNumeric} ${s.mono}`}>
                                    {formatNumber(model.output_tokens, locale)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ── Activity counts ──────────────────────────────────────

function ActivityCounts({ counts }: { counts: Record<string, number> }) {
    const { t, i18n } = useTranslation('dashboard')
    const locale = i18n.language

    const keys = Object.keys(counts)
    if (keys.length === 0) return null

    // Known operations first (in defined order), then any unknown keys.
    const orderedKeys = [
        ...OPERATION_ORDER.filter((key) => key in counts),
        ...keys.filter((key) => !(key in OPERATION_LABEL_KEYS)),
    ]

    return (
        <div className={`${layout.section} ${s.breakdownSection}`}>
            <h3 className={s.sectionTitle}>{t('usageActivityTitle')}</h3>
            <div className={s.activityGrid}>
                {orderedKeys.map((key) => {
                    const labelKey = OPERATION_LABEL_KEYS[key]
                    return (
                        <div key={key} className={s.activityItem}>
                            <span className={s.activityValue}>
                                {formatNumber(counts[key], locale)}
                            </span>
                            <span className={s.activityLabel}>
                                {labelKey ? t(labelKey) : key}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── Empty state ──────────────────────────────────────────

function EmptyState() {
    const { t } = useTranslation('dashboard')
    return (
        <div className={`${layout.section} ${s.empty}`}>
            <h3 className={s.emptyTitle}>{t('usageEmptyTitle')}</h3>
            <p className={s.emptyMessage}>{t('usageEmptyMessage')}</p>
            <button
                type="button"
                className={s.emptyCta}
                onClick={() => (window.location.href = '/sessions')}
            >
                {t('usageEmptyCta')}
            </button>
        </div>
    )
}

// ── Main page ────────────────────────────────────────────

export function Usage() {
    const { t } = useTranslation('dashboard')
    const [days, setDays] = useState<Period>(30)
    const [data, setData] = useState<UsageDashboardResponse | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [reloadKey, setReloadKey] = useState(0)

    useEffect(() => {
        let cancelled = false
        setIsLoading(true)
        setHasError(false)
        getUsageDashboard(days)
            .then((res) => {
                if (!cancelled) setData(res)
            })
            .catch(() => {
                if (!cancelled) setHasError(true)
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [days, reloadKey])

    const hasUsage =
        !!data &&
        (data.total_credits_used > 0 ||
            data.total_images_generated > 0 ||
            data.total_input_tokens > 0 ||
            data.total_output_tokens > 0)

    return (
        <div className={layout.page}>
            <div className={s.header}>
                <h3 className={layout.title}>{t('analytics')}</h3>
                <PeriodSelector value={days} onChange={setDays} disabled={isLoading} />
            </div>

            {hasError && (
                <div className={`${layout.section} ${s.errorBox}`}>
                    <p className={s.errorText}>{t('usageLoadFailed')}</p>
                    <button
                        type="button"
                        className={s.retryButton}
                        onClick={() => setReloadKey((key) => key + 1)}
                    >
                        {t('usageRetry')}
                    </button>
                </div>
            )}

            {!hasError && data && (
                <>
                    <StatTiles data={data} />
                    {hasUsage ? (
                        <>
                            <DailyCreditsChart daily={data.daily ?? []} />
                            <ToolBreakdown tools={data.by_tool ?? []} />
                            <ModelTokensTable models={data.by_model ?? []} />
                            <ActivityCounts counts={data.operation_counts ?? {}} />
                        </>
                    ) : (
                        <EmptyState />
                    )}
                </>
            )}
        </div>
    )
}
